import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../components/ui/use-toast";
import {
  Loader2,
  Save,
  Settings as SettingsIcon,
  RefreshCw,
} from "lucide-react";
import { fetchSettings, updateSettings } from "../redux/slices/settingsSlice";
import { formatDateTime } from "../lib/utils";

function Settings() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const {
    data: settings,
    loading,
    error,
  } = useSelector((state) => state.settings);
  const [localSettings, setLocalSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      // Flatten settings for easier management
      const flattened = {};
      Object.entries(settings).forEach(([category, categorySettings]) => {
        categorySettings.forEach((setting) => {
          let value = setting.value;
          if (setting.type === "boolean") {
            value = value === true || value === "true";
          } else if (setting.type === "number" && typeof value === "string") {
            const parsed = parseFloat(value);
            value = Number.isNaN(parsed) ? value : parsed;
          }
          flattened[setting.key] = {
            ...setting,
            value,
            category,
            originalValue: value,
          };
        });
      });
      setLocalSettings(flattened);
    }
  }, [settings]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const handleSettingChange = (key, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const changedSettings = Object.values(localSettings)
        .filter((setting) => setting.value !== setting.originalValue)
        .map((setting) => ({
          key: setting.key,
          value: setting.value,
          type: setting.type,
        }));

      if (changedSettings.length === 0) {
        toast({
          title: "No Changes",
          description: "No settings have been modified",
        });
        return;
      }

      await dispatch(updateSettings(changedSettings)).unwrap();

      // Update original values
      setLocalSettings((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          updated[key].originalValue = updated[key].value;
        });
        return updated;
      });

      setHasChanges(false);
      toast({
        title: "Success",
        description: `${changedSettings.length} setting(s) updated successfully`,
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalSettings((prev) => {
      const reset = { ...prev };
      Object.keys(reset).forEach((key) => {
        reset[key].value = reset[key].originalValue;
      });
      return reset;
    });
    setHasChanges(false);
    toast({
      title: "Reset",
      description: "All changes have been reset",
    });
  };

  const renderSettingInput = (setting) => {
    const { key, type, value, validation_rules } = setting;

    // Special handling for theme selection
    if (key === "default_theme") {
      const themes = [
        { value: "light", label: "Light", icon: "☀️" },
        { value: "dark", label: "Dark", icon: "🌙" },
        { value: "auto", label: "Auto", icon: "🌓" },
      ];

      return (
        <div className="flex space-x-2">
          {themes.map((theme) => (
            <button
              key={theme.value}
              onClick={() => handleSettingChange(key, theme.value)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                value === theme.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <span className="text-lg">{theme.icon}</span>
              <span className="text-sm font-medium">{theme.label}</span>
            </button>
          ))}
        </div>
      );
    }

    // Special handling for default language
    if (key === "default_language") {
      const languages = [
        { code: "en", name: "English", flag: "🇺🇸" },
        { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
        { code: "ur", name: "اردو", flag: "🇵🇰" },
        { code: "hinglish", name: "Hinglish", flag: "🇮🇳" },
        { code: "ar", name: "العربية", flag: "🇸🇦" },
        { code: "fr", name: "Français", flag: "🇫🇷" },
        { code: "es", name: "Español", flag: "🇪🇸" },
      ];

      return (
        <Select
          value={value}
          onValueChange={(value) => handleSettingChange(key, value)}
        >
          <SelectTrigger className="max-w-xs">
            <SelectValue>
              {(() => {
                const selectedLang = languages.find(
                  (lang) => lang.code === value
                );
                return selectedLang ? (
                  <div className="flex items-center space-x-2">
                    <span>{selectedLang.flag}</span>
                    <span>{selectedLang.name}</span>
                  </div>
                ) : (
                  value
                );
              })()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                <div className="flex items-center space-x-2">
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    switch (type) {
      case "boolean": {
        // API may return string "true"/"false"; non-empty strings are truthy in JS
        const boolValue = value === true || value === "true";
        return (
          <div className="flex items-center space-x-3">
            <Switch
              id={key}
              checked={boolValue}
              onCheckedChange={(checked) => handleSettingChange(key, checked)}
            />
            <Label htmlFor={key} className="text-sm font-medium">
              {boolValue ? "Enabled" : "Disabled"}
            </Label>
          </div>
        );
      }

      case "number":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) =>
              handleSettingChange(key, parseFloat(e.target.value) || 0)
            }
            className="max-w-xs"
          />
        );

      case "json":
        return (
          <Textarea
            value={
              typeof value === "string" ? value : JSON.stringify(value, null, 2)
            }
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleSettingChange(key, parsed);
              } catch {
                // Allow invalid JSON temporarily
                handleSettingChange(key, e.target.value);
              }
            }}
            className="font-mono text-sm"
            rows={3}
          />
        );

      default: // string
        if (validation_rules?.options) {
          return (
            <Select
              value={value}
              onValueChange={(value) => handleSettingChange(key, value)}
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {validation_rules.options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            value={value}
            onChange={(e) => handleSettingChange(key, e.target.value)}
            className="max-w-md"
          />
        );
    }
  };

  const groupSettingsByCategory = () => {
    const grouped = {};
    Object.values(localSettings).forEach((setting) => {
      const category = setting.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(setting);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const groupedSettings = groupSettingsByCategory();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center">
            <SettingsIcon className="h-8 w-8 mr-3" />
            Settings
          </h1>
          <p className="text-muted-foreground">
            Configure your application settings
          </p>
        </div>
        <div className="flex space-x-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} disabled={saving}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Changes Indicator */}
      {hasChanges && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center text-amber-700">
              <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium">
                You have unsaved changes
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Groups */}
      <div className="space-y-8">
        {Object.entries(groupedSettings).map(([category, categorySettings]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="capitalize text-xl">
                {category} Settings
              </CardTitle>
              <CardDescription>
                Configure {category.toLowerCase()} related preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {categorySettings.map((setting) => (
                  <div key={setting.key} className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Label className="text-base font-medium">
                          {setting.label}
                        </Label>
                        {setting.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {setting.description}
                          </p>
                        )}
                      </div>
                      <div className="ml-4">{renderSettingInput(setting)}</div>
                    </div>
                    {setting.updated_at && (
                      <p className="text-xs text-muted-foreground">
                        Last updated:{" "}
                        {formatDateTime(setting.updated_at)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default Settings;
